import { useEffect, useRef, useCallback } from 'react';

const LOCK_KEY = 'quentari_start_flow_lock';
const LOCK_TTL_MS = 10_000; // 10 seconds – aggressive TTL for fast recovery
const HEARTBEAT_INTERVAL_MS = 10_000;

interface LockEntry {
  tabId: string;
  acquiredAt: number;
  heartbeat: number;
}

function generateTabId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function readLock(): LockEntry | null {
  try {
    const raw = localStorage.getItem(LOCK_KEY);
    if (!raw) return null;
    const entry: LockEntry = JSON.parse(raw);
    // Auto-expire stale locks (tab crash recovery)
    if (Date.now() - entry.heartbeat > LOCK_TTL_MS) {
      localStorage.removeItem(LOCK_KEY);
      return null;
    }
    return entry;
  } catch {
    localStorage.removeItem(LOCK_KEY);
    return null;
  }
}

/**
 * Prevents two browser tabs from running the start-flow simultaneously.
 * Uses localStorage as a cross-tab mutex with heartbeat-based TTL.
 */
export function useStartFlowLock(workspaceId: string) {
  const tabIdRef = useRef(generateTabId());
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const acquire = useCallback((): boolean => {
    const existing = readLock();
    if (existing && existing.tabId !== tabIdRef.current) {
      // Another tab holds the lock
      return false;
    }
    const entry: LockEntry = {
      tabId: tabIdRef.current,
      acquiredAt: Date.now(),
      heartbeat: Date.now(),
    };
    localStorage.setItem(LOCK_KEY, JSON.stringify(entry));

    // Start heartbeat
    if (heartbeatRef.current) clearInterval(heartbeatRef.current);
    heartbeatRef.current = setInterval(() => {
      const current = readLock();
      if (current?.tabId === tabIdRef.current) {
        current.heartbeat = Date.now();
        localStorage.setItem(LOCK_KEY, JSON.stringify(current));
      }
    }, HEARTBEAT_INTERVAL_MS);

    return true;
  }, []);

  const release = useCallback(() => {
    const current = readLock();
    if (current?.tabId === tabIdRef.current) {
      localStorage.removeItem(LOCK_KEY);
    }
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
  }, []);

  const isLockedByOtherTab = useCallback((): boolean => {
    const existing = readLock();
    return existing !== null && existing.tabId !== tabIdRef.current;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      release();
    };
  }, [release]);

  return { acquire, release, isLockedByOtherTab };
}
